/**
 * @module app.shared
 */

import { autoinject, Loader } from 'aurelia-framework';
import { Locale } from 'date-fns';
import { AppLogger } from '../lib/logging/app-logger';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';

type LocaleLoader = () => Promise<Locale>;

const localeLoaders: Record<string, LocaleLoader> = {
    // Keep these explicit so Webpack can include only what we support.
    // Also map common aliases from i18n.
    'en': async () => enUS as Locale,
    'en-US': async () => enUS as Locale,
    'es': async () => es as Locale,
    'es-ES': async () => es as Locale,
};

@autoinject
export class DateLocaleService {

    private defaultLocale: Locale | null = enUS as Locale;
    private locales: Map<string, Locale> = new Map<string, Locale>();

    constructor(private readonly loader: Loader, private readonly logger: AppLogger) {
        this.logger.source = this;
    }

    public async load(codes: string[]): Promise<void> {
        for (const code of codes) {
            try {
                const loader = localeLoaders[code];
                if (!loader) {
                    this.logger.warn(`No locale loader registered for: ${code}`);
                    continue;
                }

                const locale = await loader();
                this.locales.set(code, locale);
                if (!this.defaultLocale) {
                    this.defaultLocale = locale;
                }

                this.logger.debug(`Loaded Date Locale: ${code}`);
            }
            catch (error) {
                this.logger.error(`Unable to load Date Locale: ${code}`, error);
            }
        }
    }

    public getLocale(code: string): Locale | null {
        const result = this.locales.get(code);
        if (result) {
            return result;
        }
        return this.defaultLocale;
    }

}
